<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, Autoplay } from 'swiper/modules'
import { LucideFlame } from 'lucide-vue-next'
import type { IResponseData } from '~/types'
import type { ITrendingNews } from './types'

import 'swiper/css/pagination'

const { data: news } = useApi<IResponseData<ITrendingNews[]>>(
  () =>
    useUrlWithParams('/news', {
      'filter[position]': 'trends',
      'filter[type]': 'news'
    }),
  {
    key: 'news.trending-news'
  }
)

const trendingNews = computed(() => news.value?.data)
const shouldEnableLoop = computed(() => (trendingNews.value?.length || 0) >= 3)
// The API may return an empty list when no news is marked as trending
const hasTrendingNews = computed(() => (trendingNews.value?.length || 0) > 0)
</script>

<template>
  <div v-if="!trendingNews" class="aspect-square rounded-lg bg-blue-50 md:bg-white" />
  <Swiper
    v-else-if="hasTrendingNews"
    :slides-per-view="1"
    :modules="[Pagination, Autoplay]"
    :auto-height="true"
    :pagination="{ clickable: true }"
    :autoplay="{ delay: 2500, pauseOnMouseEnter: true }"
    :loop="shouldEnableLoop"
    class="trending-news rounded-lg bg-blue-50 text-sm md:bg-white"
  >
    <SwiperSlide v-for="trendingNewsItem in trendingNews" :key="trendingNewsItem.id" class="p-4">
      <NuxtLinkLocale :to="trendingNewsItem.url" class="space-y-3 [&>h2]:hover:underline">
        <div class="flex items-center gap-2">
          <LucideFlame class="size-5 text-red-600" />
          {{ $t('widget.news_page.trending') }}
        </div>
        <Heading tag="h2" class="h4 line-clamp-3">{{ trendingNewsItem.title }}</Heading>
        <p class="line-clamp-3">{{ trendingNewsItem.summary }}</p>
      </NuxtLinkLocale>
    </SwiperSlide>
  </Swiper>
</template>

<style scoped>
.trending-news :deep(.swiper-pagination) {
  @apply !static !mb-4 !mt-0 min-h-5 px-4 text-left;
}
</style>
